import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Instagram, Facebook, Phone, Mail, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com' },
    { name: 'Facebook', icon: Facebook, href: 'https://facebook.com' },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Collections', path: '/collections' },
    { name: 'Hot Deals', path: '/hot-deals' },
    { name: 'Shop by Reel', path: '/reels' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact Us', path: '/contact' },
    { name: 'Your Order', path: '/order' },
  ];

  const policyLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'Exchange Policy', path: '/exchange-policy' },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&family=Cormorant+Garamond:ital,wght@0,300;1,300&display=swap');

        .ft-root {
          background: #F5F0E8;
          position: relative;
          overflow: hidden;
        }

        /* subtle grain */
        .ft-root::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='0.025'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }

        /* gold top border */
        .ft-top-border {
          height: 1px;
          background: linear-gradient(to right, transparent, #B08D57 25%, #B08D57 75%, transparent);
          opacity: 0.5;
        }

        .ft-inner {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
          padding: 64px clamp(24px, 5vw, 80px) 0;
        }

        /* ── Main grid ── */
        .ft-grid {
          display: grid;
          grid-template-columns: 1.6fr 1fr 1.4fr;
          gap: clamp(32px, 5vw, 72px);
          padding-bottom: 56px;
        }

        /* ── Brand column ── */
        .ft-logo-wrap {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }
        .ft-logo-wrap img {
          height: 52px;
          width: auto;
          object-fit: contain;
        }

        .ft-tagline {
          font-family: 'Cormorant Garamond', serif;
          font-size: 15px;
          font-style: italic;
          font-weight: 300;
          color: #5C5247;
          line-height: 1.8;
          letter-spacing: 0.02em;
          margin-bottom: 28px;
          max-width: 300px;
        }

        .ft-tagline strong {
          font-style: normal;
          font-weight: 400;
          color: #B08D57;
        }

        /* social icons */
        .ft-socials {
          display: flex;
          gap: 10px;
          margin-bottom: 0;
        }
        .ft-social-btn {
          width: 36px; height: 36px;
          border: 1px solid rgba(176,141,87,0.35);
          display: flex; align-items: center; justify-content: center;
          color: #8A7355;
          text-decoration: none;
          transition: background 0.22s, border-color 0.22s, color 0.22s;
          border-radius: 1px;
        }
        .ft-social-btn:hover {
          background: #B08D57;
          border-color: #B08D57;
          color: #FAF8F4;
        }
        .ft-social-btn svg {
          width: 15px; height: 15px;
        }

        /* ── Section heading ── */
        .ft-col-title {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #B08D57;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 22px;
        }
        .ft-col-title::after {
          content: '';
          flex: 1;
          height: 1px;
          background: linear-gradient(to right, rgba(176,141,87,0.4), transparent);
        }

        /* ── Quick links ── */
        .ft-links {
          list-style: none;
          padding: 0; margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .ft-links a {
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          font-weight: 400;
          color: #5C5247;
          text-decoration: none;
          letter-spacing: 0.04em;
          transition: color 0.2s, padding-left 0.2s;
          display: inline-block;
        }
        .ft-links a:hover {
          color: #B08D57;
          padding-left: 6px;
        }

        /* ── Contact column ── */
        .ft-contact-list {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }
        .ft-contact-row {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .ft-contact-icon {
          width: 32px; height: 32px;
          border: 1px solid rgba(176,141,87,0.3);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .ft-contact-icon svg {
          width: 13px; height: 13px;
          color: #B08D57;
        }
        .ft-contact-label {
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #B08D57;
          margin-bottom: 3px;
        }
        .ft-contact-value {
          font-family: 'Jost', sans-serif;
          font-size: 12.5px;
          font-weight: 300;
          color: #5C5247;
          line-height: 1.65;
          letter-spacing: 0.02em;
          text-decoration: none;
          transition: color 0.2s;
        }
        a.ft-contact-value:hover { color: #B08D57; }

        /* ── Divider ── */
        .ft-divider {
          height: 1px;
          background: linear-gradient(to right, transparent, rgba(176,141,87,0.3) 20%, rgba(176,141,87,0.3) 80%, transparent);
        }

        /* ── Bottom bar ── */
        .ft-bottom {
          padding: 20px clamp(24px, 5vw, 80px);
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .ft-copy {
          font-family: 'Jost', sans-serif;
          font-size: 11px;
          font-weight: 300;
          color: #9A8E80;
          letter-spacing: 0.06em;
        }
        .ft-copy span {
          color: #B08D57;
          font-weight: 500;
        }

        .ft-policy-links {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }
        .ft-policy-links a {
          font-family: 'Jost', sans-serif;
          font-size: 10px;
          font-weight: 400;
          letter-spacing: 0.1em;
          color: #9A8E80;
          text-decoration: none;
          transition: color 0.2s;
          text-transform: uppercase;
        }
        .ft-policy-links a:hover { color: #B08D57; }

        /* ── Responsive ── */
        @media (max-width: 860px) {
          .ft-grid {
            grid-template-columns: 1fr 1fr;
          }
          .ft-brand-col { grid-column: 1 / -1; }
        }
        @media (max-width: 540px) {
          .ft-grid { grid-template-columns: 1fr; }
          .ft-brand-col { grid-column: auto; }
          .ft-bottom {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
        }
      `}</style>

      <footer className="ft-root">
        <div className="ft-top-border" />

        <div className="ft-inner">
          <div className="ft-grid">

            {/* ── Brand Column ── */}
            <motion.div
              className="ft-brand-col"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
            >
              <div className="ft-logo-wrap">
                <img src="/logo.png" alt="Aamira Luxe" />
              </div>

              <p className="ft-tagline">
                Where every jewel tells a story of <strong>heritage</strong> and grace —
                crafted for women who carry elegance as their signature.
              </p>

              <div className="ft-socials">
                {socialLinks.map((s) => (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ft-social-btn"
                    aria-label={s.name}
                  >
                    <s.icon />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* ── Quick Links ── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.1 }}
            >
              <p className="ft-col-title">Navigate</p>
              <ul className="ft-links">
                {quickLinks.map((l) => (
                  <li key={l.name}>
                    <Link to={l.path}>{l.name}</Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* ── Contact ── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.2 }}
            >
              <p className="ft-col-title">Contact Us</p>
              <div className="ft-contact-list">

                <div className="ft-contact-row">
                  <div className="ft-contact-icon">
                    <Mail />
                  </div>
                  <div>
                    <p className="ft-contact-label">Email</p>
                    <a href="mailto:aamiraindia@gmail.com" className="ft-contact-value">
                      aamiraindia@gmail.com
                    </a>
                  </div>
                </div>

                <div className="ft-contact-row">
                  <div className="ft-contact-icon">
                    <MapPin />
                  </div>
                  <div>
                    <p className="ft-contact-label">Store</p>
                    <p className="ft-contact-value">
                      Aamira, 1st Floor<br />
                      Capitol Mall, Kannur<br />
                      Kerala — 670002, India
                    </p>
                  </div>
                </div>

              </div>
            </motion.div>

          </div>
        </div>

        {/* Divider */}
        <div className="ft-divider" />

        {/* Bottom bar */}
        <motion.div
          className="ft-bottom"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <p className="ft-copy">
            © {currentYear} <span>Aamira Luxe</span>. All rights reserved.
          </p>
          <div className="ft-policy-links">
            {policyLinks.map((l) => (
              <Link key={l.name} to={l.path}>{l.name}</Link>
            ))}
          </div>
        </motion.div>

      </footer>
    </>
  );
};

export default Footer;